/** Availability engine — one source of truth for "can this bay be booked then?".
 *  Mirrors the DB triggers (097/150/154 + 068): site hours ∩ bay hours, open days,
 *  blackouts, bookings + turnaround buffer, lead time, horizon. All maths in the
 *  SITE's timezone. Pure functions; callers supply the data. */
import { composeInSiteTz, decomposeInSiteTz, siteTz } from "@/lib/datetime";

export const DEAD_STATUSES = ["draft", "cancelled", "rejected", "no_show"];
const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];
const toMin = (t) => (t ? parseInt(t.slice(0, 2), 10) * 60 + parseInt(t.slice(3, 5), 10) : null);
const roundUp = (v, step) => Math.ceil(v / step) * step;

export function bayRules(site, bay, { bufferEnabled = false } = {}) {
  const slot = bay?.slot_duration_minutes || site?.slot_duration_minutes || 30;
  const bookerMin = bay?.booker_min_duration_minutes ?? site?.booker_min_duration_minutes ?? 0;
  return {
    tz: siteTz(site),
    site: [toMin(site?.operating_hours_start), toMin(site?.operating_hours_end)],
    bay: [toMin(bay?.operating_hours_start), toMin(bay?.operating_hours_end)],
    openDays: bay?.open_days || site?.open_days || ALL_DAYS,
    breaks: (bay?.daily_breaks ?? site?.daily_breaks ?? [])
      .filter((b) => b?.start && b?.end).map((b) => [toMin(b.start), toMin(b.end)]),
    slot,
    minLen: roundUp(Math.max(slot, bay?.min_booking_minutes || 0, bookerMin || 0), slot),
    leadH: bay?.min_lead_time_hours ?? site?.min_lead_time_hours ?? null,
    horizonD: bay?.max_horizon_days ?? site?.max_horizon_days ?? null,
    buffer: bufferEnabled ? (bay?.buffer_minutes ?? site?.default_buffer_minutes ?? 0) : 0,
  };
}

const FMT = new Map();
const WD = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };
function local(ms, tz) {
  let f = FMT.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
    FMT.set(tz, f);
  }
  const p = Object.fromEntries(f.formatToParts(ms).map((x) => [x.type, x.value]));
  return { dow: WD[p.weekday], min: (parseInt(p.hour, 10) % 24) * 60 + parseInt(p.minute, 10) };
}

// [open, close] in minutes; supports overnight windows; null = no limit.
function inWindow(startMin, endMin, [o, c]) {
  if (o === null || c === null) return true;
  if (o < c) return startMin >= o && endMin <= c;
  return (startMin >= o || startMin < c) && (endMin > o || endMin <= c);
}

const overlaps = (s1, e1, s2, e2) => s1 < e2 && e1 > s2;

/** State of [startMs, endMs) on one bay. bookings = that bay's live bookings. */
export function rangeState(startMs, endMs, rules, { bookings = [], blackouts = [], now = Date.now() }) {
  if (startMs < now) return "past";
  const t = (iso) => new Date(iso).getTime();
  if (bookings.some((b) => overlaps(startMs, endMs, t(b.scheduled_start), t(b.scheduled_end)))) return "booked";
  const buf = rules.buffer * 60000;
  if (buf && bookings.some((b) => overlaps(startMs, endMs + buf, t(b.scheduled_start), t(b.scheduled_end) + buf))) return "buffer";
  if (blackouts.some((x) => overlaps(startMs, endMs, t(x.start_time), t(x.end_time)))) return "blackout";
  const s = local(startMs, rules.tz);
  const e = local(endMs - 60000, rules.tz);
  const endMin = e.min + 1;
  if (!rules.openDays.includes(s.dow) || !rules.openDays.includes(e.dow)
      || !inWindow(s.min, endMin, rules.site) || !inWindow(s.min, endMin, rules.bay)) return "closed";
  const sameDay = s.dow === e.dow && endMs - startMs < 86400000;
  if (sameDay && (rules.breaks || []).some(([bs, be]) => s.min < be && endMin > bs)) return "break";
  if (rules.leadH !== null && startMs < now + rules.leadH * 3600000) return "too_soon";
  if (rules.horizonD !== null && startMs > now + rules.horizonD * 86400000) return "too_far";
  return "open";
}

/** Site-local calendar day {year, month, day} `offset` days from today. */
export function siteDay(tz, offset = 0) {
  const p = decomposeInSiteTz(new Date().toISOString(), tz);
  const d = new Date(Date.UTC(p.year, p.month - 1, p.day + offset));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

/** Start instants (ms) every `step` minutes across one site-local day. */
export function dayCells(day, tz, step) {
  const out = [];
  for (let m = 0; m < 1440; m += step) {
    out.push(new Date(composeInSiteTz({ ...day, hour: Math.floor(m / 60), minute: m % 60 }, tz)).getTime());
  }
  return out;
}

/** Bookable start times (ms) for a booker on one bay/day, for `lenMin` minutes. */
export function openStarts(day, rules, data, lenMin = rules.minLen) {
  return dayCells(day, rules.tz, rules.slot)
    .filter((s) => rangeState(s, s + lenMin * 60000, rules, data) === "open");
}

/** Closed stretches (ms intervals) of a bay inside [fromMs, toMs) — for grid shading. */
export function closedIntervals(fromMs, toMs, rules, step = 30) {
  const out = [];
  for (let s = fromMs; s < toMs; s += step * 60000) {
    const st = rangeState(s, s + step * 60000, rules, { now: 0 });
    if (st !== "closed" && st !== "break") continue;
    const last = out[out.length - 1];
    if (last && last[1] === s) last[1] = s + step * 60000;
    else out.push([s, s + step * 60000]);
  }
  return out;
}

export const STATE_STYLE = {
  open:     { cls: "bg-white hover:bg-emerald-50 hover:text-emerald-700 cursor-pointer", label: "Available" },
  booked:   { cls: "bg-rose-100 text-rose-500", label: "Booked" },
  buffer:   { cls: "bg-amber-100 text-amber-600", label: "Buffer" },
  blackout: { cls: "bg-slate-700 text-slate-200", label: "Blackout" },
  closed:   { cls: "bg-slate-200 text-slate-400", label: "Closed" },
  break:    { cls: "bg-violet-100 text-violet-500", label: "Daily break" },
  too_soon: { cls: "bg-[repeating-linear-gradient(45deg,#f1f5f9_0_4px,#e2e8f0_4px_8px)] text-slate-400", label: "Too soon" },
  too_far:  { cls: "bg-[repeating-linear-gradient(45deg,#f1f5f9_0_4px,#e2e8f0_4px_8px)] text-slate-400", label: "Too far ahead" },
  past:     { cls: "bg-slate-50 text-slate-300", label: "Past" },
};

/** Bookable (open) minutes of a bay in [fromMs, toMs): excludes closed days/hours, breaks, blackouts. */
export function openMinutes(fromMs, toMs, rules, blackouts = [], step = 30) {
  let total = 0;
  const data = { blackouts, now: 0 };
  const r = { ...rules, leadH: null, horizonD: null };
  for (let s = fromMs; s < toMs; s += step * 60000) {
    if (rangeState(s, s + step * 60000, r, data) === "open") total += step;
  }
  return total;
}
